import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CreateLoanFileSharePayload,
  CreateLoanFileShareResult,
  LoanFileShareList,
  LoanShareApiResponse,
} from '../models/loan-share.models';
import { BaseService } from './base.service';

/**
 * Loan file sharing — the member's side.
 *
 * Typed, extending BaseService, following training-admin.service.ts rather than adding url strings to
 * GenericService: the payloads here have real shapes worth naming, and the newer convention keeps
 * them honest.
 *
 * BaseService prefixes environment.apiURL and authInterceptor attaches the bearer token, so there is
 * no configuration to do here.
 *
 * There is no revoke method. Sharing is a one-way grant.
 */
@Injectable({ providedIn: 'root' })
export class LoanShareService extends BaseService {

  constructor(http: HttpClient) {
    super(http);
  }

  /** Who this file is shared with, plus the borrower addresses to suggest. One call, both answers. */
  listShares(loanapplicationId: string): Observable<LoanShareApiResponse<LoanFileShareList>> {
    return this._get(`loan-share/${loanapplicationId}`);
  }

  /**
   * Share the file with the customer: every borrower email on step 1, each invited separately.
   *
   * Takes the file id and nothing else — the recipients come from step 1 server-side.
   *
   * Sharing the same address twice is safe: the server treats it as a resend rather than failing on
   * the unique key, so a double-clicked button does not produce an error.
   */
  createShare(body: CreateLoanFileSharePayload): Observable<LoanShareApiResponse<CreateLoanFileShareResult>> {
    return this._post('loan-share', body);
  }

  /** Re-send an invite. The original link keeps working — the token is not rotated. */
  resendInvite(
    loanapplicationshare_id: string,
    loanapplication_id: string
  ): Observable<LoanShareApiResponse<{ emailsent: boolean }>> {
    return this._post('loan-share/resend', { loanapplicationshare_id, loanapplication_id });
  }
}
